/**
 * Per-display_type curated token manifests.
 *
 * Authored from the actual tokens each surface component in
 * `apps/feed-widget/src/surfaces/*` (and the Phase 2 story_block/carousel
 * components) reads. The goal is to expose only what the operator can
 * meaningfully affect on that surface — overrides to paths that don't
 * visibly apply still work at the data layer, but we hide them to keep
 * the editor calm.
 *
 * Every field:
 *   - path: dot-path into BrandingConfig (see @feed-widget/branding).
 *   - label: operator-facing label.
 *   - kind: control type — 'color' | 'size' | 'text' | 'toggle' | 'select' | 'gradient'.
 *
 * `essential` renders by default. `advanced` is behind a disclosure.
 * Adding a new surface type? Drop a new entry here and the editor picks
 * it up. Missing entry falls back to a minimal default set.
 */

import {
  BASIC_COLOR_FIELDS, STATE_COLOR_FIELDS, TYPOGRAPHY_GROUPS, TYPOGRAPHY_SUBFIELDS,
  LAYOUT_GROUPS, EFFECT_FIELDS, COMPONENT_GROUPS,
} from './brandingTokens'

const UPPERCASE_LETTERS_PATTERN = /([A-Z])/g
const WIDTH_TOKEN_SUFFIX_PATTERN = /Width$/
const COLOR_TOKEN_NAME_PATTERN = /Color$|^color$/
const COLOR_TOKEN_SUFFIX_PATTERN = /color$|background$/i

const PREPACK_LAYOUT_PATHS = new Set([
  'layout.feed.paddingX', 'layout.feed.paddingY', 'layout.feed.cardGap',
  'layout.card.borderWidth', 'layout.card.borderRadius', 'layout.card.contentPaddingX',
  'layout.card.contentPaddingY', 'layout.card.contentPaddingTop', 'layout.card.contentPaddingBottom',
  'layout.cta.actionsPaddingX', 'layout.cta.actionsPaddingY', 'layout.cta.paddingX',
  'layout.cta.paddingY', 'layout.cta.borderRadius', 'layout.cta.buttonHeight',
  'layout.compactCard.widthMobile', 'layout.compactCard.widthDesktop',
])

export const PREPACK_TOKEN_MANIFEST = {
  label: 'Prepacks',
  description: 'Surface defaults and delivered color mode. Explicit template variant styles take precedence; edit those in Prepacks → Templates. Set the action label in Prepacks surface settings.',
  essential: [
    { path: 'darkMode', label: 'Dark card variant', kind: 'toggle' },
    ...BASIC_COLOR_FIELDS.map((field) => ({ ...field, kind: 'color' })),
    { path: 'colors.surface', label: 'Card background', kind: 'color' },
    { path: 'fonts.primary', label: 'Primary font', kind: 'text' },
    { path: 'layout.card.borderRadius', label: 'Card radius', kind: 'size' },
    { path: 'layout.feed.cardGap', label: 'Card gap', kind: 'size' },
    { path: 'layout.compactCard.widthMobile', label: 'Card width (mobile)', kind: 'size' },
    { path: 'layout.compactCard.widthDesktop', label: 'Card width (desktop)', kind: 'size' },
  ],
  advanced: [
    ...STATE_COLOR_FIELDS.filter((field) => ['colors.success', 'colors.error'].includes(field.path))
      .map((field) => ({ ...field, kind: 'color' })),
    { path: 'borderWidth', label: 'Default border width', kind: 'size' },
    ...TYPOGRAPHY_GROUPS.filter((group) => ['body', 'betLabel', 'legSelection', 'legMarket', 'odds', 'cta'].includes(group.path.split('.')[1]))
      .flatMap((group) => TYPOGRAPHY_SUBFIELDS.map((field) => ({
        ...field, path: `${group.path}.${field.key}`, label: `${group.label}: ${field.label}`,
      }))),
    ...LAYOUT_GROUPS.flatMap((group) => group.fields)
      .filter((field) => PREPACK_LAYOUT_PATHS.has(field.path)
        && !['layout.card.borderRadius', 'layout.feed.cardGap', 'layout.compactCard.widthMobile', 'layout.compactCard.widthDesktop'].includes(field.path))
      .map((field) => ({ ...field, kind: 'size' })),
    ...EFFECT_FIELDS.filter((field) => ['effects.cardShadow', 'effects.cardHoverShadow', 'effects.cardHoverLift', 'effects.ctaShadow'].includes(field.path)),
    ...[
      'background', 'color', 'borderWidth', 'borderColor', 'hoverBackground',
      'hoverBorderColor', 'hoverOpacity', 'activeBackground', 'activeColor',
      'activeBorderColor', 'addedBackground', 'addedColor', 'addedBorderColor',
      'borderBottomWidth', 'disabledOpacity',
    ].map((key) => ({
      path: `components.cta.${key}`,
      label: `CTA ${key.replace(UPPERCASE_LETTERS_PATTERN, ' $1').toLowerCase()}`,
      kind: WIDTH_TOKEN_SUFFIX_PATTERN.test(key) ? 'size' : COLOR_TOKEN_NAME_PATTERN.test(key) ? 'color' : 'text',
    })),
    ...COMPONENT_GROUPS.flatMap((group) => group.fields)
      .filter((field) => field.path.startsWith('components.prepackActions.') || field.path.startsWith('components.prepackOdds.')),
    ...['background', 'color', 'highlightColor', 'borderRadius', 'paddingX', 'paddingY', 'marginBottom'].map((key) => ({
      path: `components.surfaceHeading.${key}`,
      label: `Heading ${key.replace(UPPERCASE_LETTERS_PATTERN, ' $1').toLowerCase()}`,
      kind: COLOR_TOKEN_SUFFIX_PATTERN.test(key) ? 'color' : 'size',
    })),
  ],
}

const SURFACE_HEADING_FIELDS = [
  { path: 'components.surfaceHeading.color', label: 'Heading text', kind: 'color' },
  { path: 'components.surfaceHeading.background', label: 'Heading background', kind: 'color' },
  { path: 'components.surfaceHeading.fontSize', label: 'Heading size', kind: 'size' },
  { path: 'components.surfaceHeading.borderRadius', label: 'Heading radius', kind: 'size' },
]

export const FALLBACK_MANIFEST = {
  label: 'Feed surface',
  description: 'Generic per-surface overrides.',
  essential: [
    ...SURFACE_HEADING_FIELDS,
    { path: 'colors.primary', label: 'Accent', kind: 'color' },
    { path: 'colors.background', label: 'Background', kind: 'color' },
    { path: 'layout.card.borderRadius', label: 'Card radius', kind: 'size' },
  ],
  advanced: [
    { path: 'layout.card.borderWidth', label: 'Card border', kind: 'size' },
    { path: 'effects.cardShadow', label: 'Card shadow', kind: 'text' },
  ],
}

export const SURFACE_TOKEN_MANIFESTS = {
  story_block: {
    label: 'Story block',
    description: 'Vertical scroll feed of full-width cards.',
    essential: [
      ...SURFACE_HEADING_FIELDS,
      { path: 'colors.background', label: 'Background', kind: 'color' },
      { path: 'colors.primary', label: 'Accent', kind: 'color' },
      { path: 'colors.heading', label: 'Heading', kind: 'color' },
      { path: 'colors.subheading', label: 'Subheading', kind: 'color' },
      { path: 'layout.feed.maxWidth', label: 'Feed max width', kind: 'size' },
      { path: 'layout.feed.maxWidthDesktop', label: 'Desktop feed max width', kind: 'size' },
      { path: 'layout.feed.cardGap', label: 'Card gap', kind: 'size' },
      { path: 'layout.card.borderRadius', label: 'Card radius', kind: 'size' },
      { path: 'effects.cardShadow', label: 'Card shadow', kind: 'text' },
    ],
    advanced: [
      { path: 'layout.feed.paddingX', label: 'Feed horizontal padding', kind: 'size' },
      { path: 'layout.feed.paddingY', label: 'Feed vertical padding', kind: 'size' },
      { path: 'layout.feed.bannerGap', label: 'Banner gap', kind: 'size' },
      { path: 'layout.card.borderWidth', label: 'Card border', kind: 'size' },
      { path: 'layout.card.headerPaddingX', label: 'Header padding X', kind: 'size' },
      { path: 'layout.card.contentPaddingX', label: 'Content padding X', kind: 'size' },
      { path: 'layout.card.contentPaddingTop', label: 'Content padding top', kind: 'size' },
      { path: 'layout.card.contentPaddingBottom', label: 'Content padding bottom', kind: 'size' },
      { path: 'typography.username.fontSizeDesktop', label: 'Desktop username size', kind: 'size' },
      { path: 'typography.username.lineHeightDesktop', label: 'Desktop username line height', kind: 'text' },
      { path: 'typography.body.fontSizeDesktop', label: 'Desktop body size', kind: 'size' },
      { path: 'typography.body.lineHeightDesktop', label: 'Desktop body line height', kind: 'text' },
      { path: 'typography.betLabel.fontSizeDesktop', label: 'Desktop bet label size', kind: 'size' },
      { path: 'typography.betLabel.lineHeightDesktop', label: 'Desktop bet label line height', kind: 'text' },
      { path: 'typography.odds.fontSizeDesktop', label: 'Desktop odds size', kind: 'size' },
      { path: 'typography.odds.lineHeightDesktop', label: 'Desktop odds line height', kind: 'text' },
      { path: 'typography.legMarket.fontSizeDesktop', label: 'Desktop leg market size', kind: 'size' },
      { path: 'typography.legMarket.lineHeightDesktop', label: 'Desktop leg market line height', kind: 'text' },
      { path: 'typography.legSelection.fontSizeDesktop', label: 'Desktop leg selection size', kind: 'size' },
      { path: 'typography.legSelection.lineHeightDesktop', label: 'Desktop leg selection line height', kind: 'text' },
      { path: 'typography.cta.fontSizeDesktop', label: 'Desktop CTA size', kind: 'size' },
      { path: 'typography.cta.lineHeightDesktop', label: 'Desktop CTA line height', kind: 'text' },
      { path: 'layout.media.videoMaxHeight', label: 'Video max height', kind: 'size' },
      { path: 'effects.cardHoverShadow', label: 'Card hover shadow', kind: 'text' },
      { path: 'effects.cardHoverLift', label: 'Card hover lift', kind: 'size' },
      { path: 'effects.ctaShadow', label: 'CTA shadow', kind: 'text' },
      { path: 'presentation.showFollowButton', label: 'Show follow button', kind: 'toggle' },
      {
        path: 'presentation.shareLabelMode',
        label: 'Share label',
        kind: 'select',
        options: [
          { value: 'responsive', label: 'Hide on narrow screens' },
          { value: 'always', label: 'Always show' },
          { value: 'hidden', label: 'Always hide' },
        ],
      },
      {
        path: 'presentation.betslip.variant',
        label: 'Betslip layout',
        kind: 'select',
        options: [
          { value: 'rows', label: 'Rows' },
          { value: 'panel', label: 'Panel' },
        ],
      },
      { path: 'presentation.betslip.labelTemplate', label: 'Betslip label template', kind: 'text' },
      { path: 'presentation.betslip.parlayLabelTemplate', label: 'Parlay label template', kind: 'text' },
      { path: 'presentation.betslip.showSingleLabel', label: 'Show single label', kind: 'toggle' },
      { path: 'presentation.betslip.showToplineEvent', label: 'Show topline event', kind: 'toggle' },
      { path: 'presentation.betslip.showGroupLabel', label: 'Show group label', kind: 'toggle' },
      { path: 'presentation.betslip.showLegEvent', label: 'Show event on each leg', kind: 'toggle' },
      { path: 'presentation.betslip.connectMultipleSingles', label: 'Connect multiple singles', kind: 'toggle' },
      {
        path: 'presentation.betslip.badgePosition',
        label: 'SGP badge position',
        kind: 'select',
        options: [
          { value: 'after-label', label: 'After label' },
          { value: 'before-label', label: 'Before label' },
        ],
      },
      {
        path: 'presentation.betslip.oddsPosition',
        label: 'Combined odds position',
        kind: 'select',
        options: [
          { value: 'trailing', label: 'Trailing' },
          { value: 'inline', label: 'Inline' },
        ],
      },
      { path: 'layout.betslip.panelPaddingX', label: 'Betslip panel padding X', kind: 'size' },
      { path: 'layout.betslip.panelPaddingY', label: 'Betslip panel padding Y', kind: 'size' },
      { path: 'layout.betslip.panelGap', label: 'Betslip panel gap', kind: 'size' },
      { path: 'layout.betslip.panelBorderRadius', label: 'Betslip panel radius', kind: 'size' },
      { path: 'layout.betslip.panelMinHeight', label: 'Betslip panel minimum height', kind: 'size' },
      { path: 'layout.betslip.rowGap', label: 'Betslip row gap', kind: 'size' },
      { path: 'layout.cta.buttonTopRadius', label: 'CTA top radius', kind: 'size' },
      { path: 'components.cta.background', label: 'CTA background', kind: 'text' },
      { path: 'components.cta.borderBottomWidth', label: 'CTA bottom border', kind: 'size' },
      { path: 'components.shareButton.background', label: 'Share background', kind: 'text' },
      { path: 'components.shareButton.color', label: 'Share text', kind: 'color' },
      { path: 'components.shareButton.borderWidth', label: 'Share border', kind: 'size' },
      { path: 'components.shareButton.borderRadius', label: 'Share radius', kind: 'size' },
      { path: 'components.betslip.panelBackground', label: 'Betslip panel background', kind: 'color' },
      { path: 'components.betslip.panelBorderColor', label: 'Betslip panel border', kind: 'color' },
    ],
  },

  carousel: {
    label: 'Carousel',
    description: '3D deck of compact cards on desktop, swipe on mobile.',
    essential: [
      ...SURFACE_HEADING_FIELDS,
      { path: 'colors.primary', label: 'Accent', kind: 'color' },
      { path: 'components.compactCard.background', label: 'Card background', kind: 'gradient' },
      { path: 'layout.compactCard.widthDesktop', label: 'Card width (desktop)', kind: 'size' },
      { path: 'layout.compactCard.heightDesktop', label: 'Card height (desktop)', kind: 'size' },
      { path: 'layout.compactCard.borderRadius', label: 'Card radius', kind: 'size' },
      { path: 'components.compactCard.overlayTextColor', label: 'Overlay text', kind: 'color' },
      { path: 'effects.compactBackdrop', label: 'Card backdrop', kind: 'text' },
    ],
    advanced: [
      { path: 'layout.compactCard.widthMobile', label: 'Card width (mobile)', kind: 'size' },
      { path: 'layout.compactCard.heightMobile', label: 'Card height (mobile)', kind: 'size' },
      { path: 'layout.compactCard.borderWidth', label: 'Card border', kind: 'size' },
      { path: 'components.compactCard.borderColor', label: 'Card border color', kind: 'color' },
      { path: 'components.compactCard.boxShadow', label: 'Card shadow', kind: 'text' },
      { path: 'layout.compactCard.avatarSize', label: 'Avatar size', kind: 'size' },
      { path: 'components.compactCard.avatarBorderColor', label: 'Avatar border', kind: 'color' },
      { path: 'components.compactCard.avatarBorderWidth', label: 'Avatar border width', kind: 'size' },
      { path: 'effects.compactDrawerShadow', label: 'Drawer shadow', kind: 'text' },
    ],
  },

  grid: {
    label: 'Grid',
    description: 'Responsive tile grid with equal-weight cards.',
    essential: [
      ...SURFACE_HEADING_FIELDS,
      { path: 'colors.primary', label: 'Accent', kind: 'color' },
      { path: 'colors.background', label: 'Tile background', kind: 'color' },
      { path: 'layout.card.borderRadius', label: 'Tile radius', kind: 'size' },
      { path: 'layout.media.gridGap', label: 'Tile gap', kind: 'size' },
      { path: 'effects.cardShadow', label: 'Tile shadow', kind: 'text' },
    ],
    advanced: [
      { path: 'layout.card.borderWidth', label: 'Tile border', kind: 'size' },
      { path: 'layout.card.headerPaddingX', label: 'Tile header padding', kind: 'size' },
      { path: 'layout.card.contentPaddingX', label: 'Tile content padding', kind: 'size' },
      { path: 'effects.cardHoverShadow', label: 'Hover shadow', kind: 'text' },
      { path: 'effects.cardHoverLift', label: 'Hover lift', kind: 'size' },
    ],
  },

  hero: {
    label: 'Hero',
    description: 'Single viewport card with arrows and dot navigation.',
    essential: [
      ...SURFACE_HEADING_FIELDS,
      { path: 'colors.primary', label: 'Accent (dots)', kind: 'color' },
      { path: 'colors.background', label: 'Background', kind: 'color' },
      { path: 'layout.card.borderRadius', label: 'Card radius', kind: 'size' },
      { path: 'effects.cardShadow', label: 'Card shadow', kind: 'text' },
    ],
    advanced: [
      { path: 'layout.media.videoMaxHeight', label: 'Media max height', kind: 'size' },
      { path: 'layout.card.borderWidth', label: 'Card border', kind: 'size' },
      { path: 'colors.subheading', label: 'Dot inactive', kind: 'color' },
    ],
  },

  circle_stories: {
    label: 'Circle stories',
    description: 'Avatar row with tap-to-open full-bleed viewer.',
    essential: [
      ...SURFACE_HEADING_FIELDS,
      { path: 'colors.primary', label: 'Ring accent', kind: 'color' },
      { path: 'colors.secondary', label: 'Ring accent (end)', kind: 'color' },
      { path: 'colors.subheading', label: 'Story label', kind: 'color' },
      { path: 'colors.background', label: 'Viewer backdrop', kind: 'color' },
    ],
    advanced: [
      { path: 'layout.header.avatarSize', label: 'Avatar size', kind: 'size' },
      { path: 'colors.heading', label: 'Avatar fallback text', kind: 'color' },
      { path: 'effects.avatarShadow', label: 'Avatar shadow', kind: 'text' },
    ],
  },

  floating_player: {
    label: 'Floating player',
    description: 'Picture-in-picture tile pinned to the viewport corner.',
    essential: [
      ...SURFACE_HEADING_FIELDS,
      { path: 'colors.background', label: 'PiP background', kind: 'color' },
      { path: 'colors.primary', label: 'Dismiss accent', kind: 'color' },
      { path: 'layout.card.borderRadius', label: 'PiP radius', kind: 'size' },
      { path: 'effects.cardShadow', label: 'PiP shadow', kind: 'text' },
    ],
    advanced: [
      { path: 'colors.heading', label: 'Controls color', kind: 'color' },
      { path: 'effects.mediaControlBackdrop', label: 'Controls backdrop', kind: 'text' },
    ],
  },

  player_deck: {
    label: 'Player deck',
    description: 'Fanned stack of cards, tap to advance.',
    essential: [
      ...SURFACE_HEADING_FIELDS,
      { path: 'colors.primary', label: 'Focus outline', kind: 'color' },
      { path: 'colors.subheading', label: 'Counter text', kind: 'color' },
      { path: 'layout.card.borderRadius', label: 'Card radius', kind: 'size' },
      { path: 'effects.cardShadow', label: 'Card shadow', kind: 'text' },
    ],
    advanced: [
      { path: 'colors.background', label: 'Deck background', kind: 'color' },
      { path: 'effects.cardHoverShadow', label: 'Hover shadow', kind: 'text' },
    ],
  },

  horizontal_player: {
    label: 'Horizontal player',
    description: 'Single wide featured card with a dominant play button.',
    essential: [
      ...SURFACE_HEADING_FIELDS,
      { path: 'colors.primary', label: 'Play accent', kind: 'color' },
      { path: 'layout.card.borderRadius', label: 'Card radius', kind: 'size' },
      { path: 'effects.playOverlayBackdrop', label: 'Play overlay', kind: 'text' },
      { path: 'effects.cardShadow', label: 'Card shadow', kind: 'text' },
    ],
    advanced: [
      { path: 'layout.media.playOverlaySize', label: 'Play button size', kind: 'size' },
      { path: 'layout.media.controlButtonSize', label: 'Control button size', kind: 'size' },
    ],
  },

  horizontal_carousel: {
    label: 'Horizontal carousel',
    description: 'Row of three snap-scroll cards.',
    essential: [
      ...SURFACE_HEADING_FIELDS,
      { path: 'colors.primary', label: 'Accent', kind: 'color' },
      { path: 'components.compactCard.background', label: 'Card background', kind: 'gradient' },
      { path: 'layout.compactCard.widthDesktop', label: 'Card width', kind: 'size' },
      { path: 'layout.compactCard.borderRadius', label: 'Card radius', kind: 'size' },
      { path: 'layout.media.gridGap', label: 'Card gap', kind: 'size' },
    ],
    advanced: [
      { path: 'layout.compactCard.heightDesktop', label: 'Card height (desktop)', kind: 'size' },
      { path: 'layout.compactCard.widthMobile', label: 'Card width (mobile)', kind: 'size' },
      { path: 'layout.compactCard.borderWidth', label: 'Card border', kind: 'size' },
      { path: 'components.compactCard.borderColor', label: 'Card border color', kind: 'color' },
      { path: 'components.compactCard.boxShadow', label: 'Card shadow', kind: 'text' },
      { path: 'components.compactCard.overlayTextColor', label: 'Overlay text', kind: 'color' },
    ],
  },

  dynamic_carousel: {
    label: 'Dynamic carousel',
    description: 'Asymmetric — one featured card plus two smaller thumbnails.',
    essential: [
      ...SURFACE_HEADING_FIELDS,
      { path: 'colors.primary', label: 'Focus accent', kind: 'color' },
      { path: 'layout.card.borderRadius', label: 'Featured card radius', kind: 'size' },
      { path: 'layout.compactCard.borderRadius', label: 'Thumbnail radius', kind: 'size' },
      { path: 'layout.media.gridGap', label: 'Column gap', kind: 'size' },
    ],
    advanced: [
      { path: 'effects.cardShadow', label: 'Featured card shadow', kind: 'text' },
      { path: 'components.compactCard.background', label: 'Thumbnail background', kind: 'gradient' },
      { path: 'layout.compactCard.borderWidth', label: 'Thumbnail border', kind: 'size' },
      { path: 'components.compactCard.borderColor', label: 'Thumbnail border color', kind: 'color' },
      { path: 'components.compactCard.boxShadow', label: 'Thumbnail shadow', kind: 'text' },
      { path: 'effects.compactDrawerShadow', label: 'Drawer shadow', kind: 'text' },
      { path: 'components.compactCard.overlayTextColor', label: 'Thumbnail overlay text', kind: 'color' },
    ],
  },

  vertical_stories: {
    label: 'Vertical stories',
    description: 'Full-bleed vertical viewer, one post per screen.',
    essential: [
      ...SURFACE_HEADING_FIELDS,
      { path: 'colors.background', label: 'Background', kind: 'color' },
      { path: 'colors.heading', label: 'Progress counter', kind: 'color' },
      { path: 'colors.primary', label: 'CTA accent', kind: 'color' },
      { path: 'layout.card.borderRadius', label: 'Card radius', kind: 'size' },
    ],
    advanced: [
      { path: 'effects.overlayTextShadow', label: 'Overlay text shadow', kind: 'text' },
      { path: 'effects.playOverlayBackdrop', label: 'Play overlay', kind: 'text' },
    ],
  },
}

export function getSurfaceManifest(displayType, contentMode = 'feed') {
  if (contentMode === 'prepacks') return PREPACK_TOKEN_MANIFEST
  return SURFACE_TOKEN_MANIFESTS[displayType] || FALLBACK_MANIFEST
}

/**
 * Returns every token path this display_type exposes (essential + advanced).
 * Used to warn when theme_overrides contains paths outside the manifest.
 */
export function getAllSurfaceTokenPaths(displayType, contentMode = 'feed') {
  const manifest = getSurfaceManifest(displayType, contentMode)
  return [
    ...manifest.essential.map((field) => field.path),
    ...manifest.advanced.map((field) => field.path),
  ]
}
